"use client";

import React, { useState } from "react";
import { usePathname } from "next/navigation";
import { SidebarTrigger } from "./ui/sidebar";
import { Separator } from "@radix-ui/react-separator";
import { ThemeToggle } from "./theme-toggle";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { Bell, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

const breadcrumbTitles: Record<string, string> = {
  dashboard: "Dashboard",
  products: "Products",
  users: "Users",
  settings: "Settings",
  profile: "Profile",
  billing: "Billing",
  notifications: "Notifications",
};

export default function Header() {
  const pathname = usePathname();

  if (!pathname || pathname === "/404") return null; // Don't render on 404 pages

  const pathSegments = pathname.split("/").filter(Boolean);

  // State for managing notification panel visibility
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, message: "New user registered", time: "2 mins ago" },
    { id: 2, message: "Order #1234 has been shipped", time: "15 mins ago" },
    { id: 3, message: "New comment on your post", time: "1 hour ago" },
  ]);

  return (
    <header className="flex h-16 shrink-0 items-center justify-between transition-all ease-linear">
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/dashboard"
                className="text-[#18181a] dark:text-[#e7e7e4]"
              >
                Dashboard
              </BreadcrumbLink>
            </BreadcrumbItem>

            {pathSegments[0] !== "dashboard" &&
              pathSegments.map((segment, index) => {
                const href = `/${pathSegments.slice(0, index + 1).join("/")}`;
                const isLast = index === pathSegments.length - 1;
                const title =
                  breadcrumbTitles[segment] || segment.replace(/-/g, " ");

                return (
                  <React.Fragment key={href}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage className="text-[#18181a] dark:text-[#e7e7e4]">
                          {title}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink
                          href={href}
                          className="text-[#18181a] dark:text-[#e7e7e4]"
                        >
                          {title}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                );
              })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="relative flex items-center gap-4 px-4">
        {/* Notification Button */}
        <Button
          variant="ghost"
          className="relative"
          onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
        >
          <Bell className="h-5 w-5" />
          {notifications.length > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {notifications.length}
            </span>
          )}
        </Button>

        {/* Notification Panel */}
        {isNotificationPanelOpen && (
          <Card className="absolute right-4 top-16 w-80 z-50">
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Notifications</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsNotificationPanelOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-60">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="mb-2 rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {notification.time}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No notifications
                  </p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Theme Toggle */}
        <ThemeToggle />
      </div>
    </header>
  );
}
